import { useEffect, useRef } from 'react';

interface TranscriptEntry {
  id: string;
  timestamp: string;
  text: string;
  speaker?: string;
}

interface TranscriptPanelProps {
  transcript: TranscriptEntry[];
  isRecording: boolean;
  onClear: () => void;
  onExport: (format: string) => void;
}

const TranscriptPanel = ({ transcript, isRecording, onClear, onExport }: TranscriptPanelProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  if (transcript.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-mic-2-line text-3xl text-teal-500"></i>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Transcript Yet</h3>
        <p className="text-sm text-slate-500 max-w-md">
          Press <span className="font-semibold text-teal-600">Start Recording</span> to begin capturing and transcribing your session in real time.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
            {transcript.length} segments
          </span>
          {isRecording && (
            <span className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Live
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onExport('txt')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-line"></i>
            Export
          </button>
          <button
            onClick={onClear}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 text-red-500 rounded-lg text-xs font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Clear
          </button>
        </div>
      </div>

      {/* Entries */}
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
        {transcript.map((entry) => (
          <div
            key={entry.id}
            className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow"
          >
            <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-slate-100">
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-slate-400 text-xs"></i>
                <span className="text-xs font-mono text-slate-400">{entry.timestamp}</span>
                {entry.speaker && (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                    {entry.speaker}
                  </span>
                )}
              </div>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm text-slate-700 leading-relaxed">{entry.text}</p>
            </div>
          </div>
        ))}

        {/* Live typing indicator */}
        {isRecording && (
          <div className="bg-white rounded-xl border border-teal-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold text-slate-500">Transcribing...</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Footer summary */}
      {transcript.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-400">
          <span>{transcript.reduce((acc, e) => acc + e.text.split(' ').length, 0)} words captured</span>
          <span>{transcript.length} segments recorded</span>
        </div>
      )}
    </div>
  );
};

export default TranscriptPanel;
